import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, reportsApi, devicesApi, geocodingApi, publicApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';
import { fr, de, enUS, it } from 'date-fns/locale';

const DATE_LOCALES: Record<string, typeof fr> = { fr, de, en: enUS, it };
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FileText,
  Route,
  StopCircle,
  BarChart3,
  AlertTriangle,
  Clock,
  Gauge,
  MapPin,
  Loader2,
  ChevronDown,
  FileSpreadsheet,
  FilePlus,
  TrendingUp,
  Calendar,
  Truck,
  CalendarClock,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

type ReportType = 'summary' | 'trips' | 'stops' | 'events';

interface Trip {
  deviceId: string;
  deviceName: string;
  startTime: string;
  endTime: string;
  startLatitude: number;
  startLongitude: number;
  endLatitude: number;
  endLongitude: number;
  startAddress?: string;
  endAddress?: string;
  distance: number;
  duration: number;
  maxSpeed: number;
  avgSpeed: number;
  positionCount: number;
}

interface Stop {
  deviceId: string;
  deviceName: string;
  startTime: string;
  endTime: string;
  latitude: number;
  longitude: number;
  address?: string;
  duration: number;
}

interface Summary {
  deviceId: string;
  deviceName: string;
  plate?: string;
  distance: number;
  tripCount: number;
  stopCount: number;
  movingDuration: number;
  stoppedDuration: number;
  maxSpeed: number;
  avgSpeed: number;
  fuelConsumed?: number;
  energyConsumed?: number;
  vehicleType?: string;
}

interface Event {
  id: string;
  deviceId: string;
  deviceName: string;
  type: string;
  timestamp: string;
  latitude?: number;
  longitude?: number;
}

// `label`/`description` hold i18n keys resolved with t() at render time.
const reportTypes = [
  { id: 'summary' as ReportType, label: 'reports.type_summary_label', icon: BarChart3, description: 'reports.type_summary_desc' },
  { id: 'trips' as ReportType, label: 'reports.type_trips_label', icon: Route, description: 'reports.type_trips_desc' },
  { id: 'stops' as ReportType, label: 'reports.type_stops_label', icon: StopCircle, description: 'reports.type_stops_desc' },
  { id: 'events' as ReportType, label: 'reports.type_events_label', icon: AlertTriangle, description: 'reports.type_events_desc' },
];

const datePresets = [
  { label: 'reports.preset_today', getValue: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }) },
  { label: 'reports.preset_yesterday', getValue: () => ({ start: startOfDay(subDays(new Date(), 1)), end: endOfDay(subDays(new Date(), 1)) }) },
  { label: 'reports.preset_7d', getValue: () => ({ start: startOfDay(subDays(new Date(), 7)), end: endOfDay(new Date()) }) },
  { label: 'reports.preset_30d', getValue: () => ({ start: startOfDay(subDays(new Date(), 30)), end: endOfDay(new Date()) }) },
];

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatDate(dateStr: string): string {
  return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: fr });
}

// Custom tooltip for charts
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-gray-50/95 backdrop-blur-sm border border-gray-200 rounded-lg p-3 shadow-xl">
      <p className="text-gray-600 text-sm mb-2">{label}</p>
      {payload.map((item, index) => (
        <p key={index} className="text-sm" style={{ color: item.color }}>
          {item.name}: <span className="font-semibold text-white">{typeof item.value === 'number' ? item.value.toFixed(1) : item.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [reportType, setReportType] = useState<ReportType>('summary');
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({
    start: startOfDay(subDays(new Date(), 7)),
    end: endOfDay(new Date()),
  });
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [showCharts, setShowCharts] = useState(true);
  const reportRef = useRef<HTMLDivElement>(null);
  const logoDataRef = useRef<string | null>(null);
  const deviceSelectorRef = useRef<HTMLDivElement>(null);

  // Close device selector on outside click
  useEffect(() => {
    if (!showDeviceSelector) return;
    const handler = (e: MouseEvent) => {
      if (deviceSelectorRef.current && !deviceSelectorRef.current.contains(e.target as Node)) {
        setShowDeviceSelector(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDeviceSelector]);

  // Fetch logo for PDF export
  useEffect(() => {
    publicApi.getAppearance().then((appearance) => {
      if (appearance?.logo) {
        logoDataRef.current = appearance.logo;
      }
    }).catch(() => {});
  }, []);

  // Fetch devices
  const { data: devicesData } = useQuery({
    queryKey: ['devices', tenantKey],
    queryFn: () => devicesApi.list({ limit: 100 }),
  });
  const devices = devicesData?.data?.data || [];

  // Build query params
  const queryParams = {
    deviceIds: selectedDevices.length > 0 ? selectedDevices : undefined,
    startDate: dateRange.start.toISOString(),
    endDate: dateRange.end.toISOString(),
  };

  // Fetch report data based on type
  const { data: reportData, isLoading, refetch } = useQuery({
    queryKey: ['report', reportType, queryParams, tenantKey],
    queryFn: async () => {
      switch (reportType) {
        case 'summary':
          return reportsApi.getSummary(queryParams);
        case 'trips':
          return reportsApi.getTrips(queryParams);
        case 'stops':
          return reportsApi.getStops(queryParams);
        case 'events':
          return reportsApi.getEvents(queryParams);
      }
    },
    enabled: true,
  });

  const data = useMemo(() => reportData?.data?.data || [], [reportData]);

  // Address resolution for trips and stops
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  const [addressesLoading, setAddressesLoading] = useState(false);
  const resolvedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (data.length === 0 || (reportType !== 'trips' && reportType !== 'stops')) return;

    const coords: Array<{ lat: number; lng: number }> = [];
    const seen = new Set<string>();

    const addCoord = (lat: number, lng: number) => {
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      if (!seen.has(key) && !resolvedKeysRef.current.has(key)) {
        seen.add(key);
        coords.push({ lat, lng });
      }
    };

    if (reportType === 'trips') {
      for (const trip of data as Trip[]) {
        if (trip.startLatitude && trip.startLongitude) addCoord(trip.startLatitude, trip.startLongitude);
        if (trip.endLatitude && trip.endLongitude) addCoord(trip.endLatitude, trip.endLongitude);
      }
    } else {
      for (const stop of data as Stop[]) {
        if (stop.latitude && stop.longitude) addCoord(stop.latitude, stop.longitude);
      }
    }

    if (coords.length === 0) return;

    let cancelled = false;
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 4000;

    const resolve = async (attempt = 0) => {
      setAddressesLoading(true);
      try {
        const res = await geocodingApi.batch(coords);
        if (cancelled) return;
        for (const key of Object.keys(res.data.addresses)) {
          resolvedKeysRef.current.add(key);
        }
        setAddresses((prev) => ({ ...prev, ...res.data.addresses }));

        // Retry if there are still pending addresses being resolved in background
        const pending = res.data.pending || 0;
        if (pending > 0 && attempt < MAX_RETRIES) {
          setTimeout(() => {
            if (!cancelled) resolve(attempt + 1);
          }, RETRY_DELAY_MS);
          return; // keep addressesLoading true
        }
      } catch (err) {
        console.error('Erreur résolution adresses:', err);
      }
      if (!cancelled) setAddressesLoading(false);
    };
    resolve();

    return () => { cancelled = true; };
  }, [reportType, data]);

  const getAddress = (lat: number, lng: number): string | undefined => {
    return addresses[`${lat.toFixed(5)},${lng.toFixed(5)}`];
  };

  // Generate chart data for summary
  const distanceChartData = (data as Summary[]).map((item) => ({
    name: item.deviceName.length > 10 ? item.deviceName.substring(0, 10) + '...' : item.deviceName,
    distance: item.distance,
    trajets: item.tripCount,
  }));

  const timeDistributionData = (data as Summary[]).reduce(
    (acc, item) => {
      acc[0].value += item.movingDuration;
      acc[1].value += item.stoppedDuration;
      return acc;
    },
    [
      { name: 'En mouvement', value: 0 },
      { name: 'A l\'arret', value: 0 },
    ]
  );

  // Generate daily distance chart data for trips
  const dailyDistanceData = (() => {
    if (reportType !== 'trips' || data.length === 0) return [];

    const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
    return days.map((day) => {
      const dayTrips = (data as Trip[]).filter((trip) => {
        const tripDate = new Date(trip.startTime);
        return (
          tripDate.getDate() === day.getDate() &&
          tripDate.getMonth() === day.getMonth() &&
          tripDate.getFullYear() === day.getFullYear()
        );
      });

      return {
        date: format(day, 'dd/MM', { locale: fr }),
        distance: dayTrips.reduce((acc, t) => acc + t.distance, 0),
        trajets: dayTrips.length,
      };
    });
  })();

  const handleDatePreset = (preset: typeof datePresets[0]) => {
    const { start, end } = preset.getValue();
    setDateRange({ start, end });
  };

  const handleExportExcel = async () => {
    let exportUrl: string;

    switch (reportType) {
      case 'summary':
        exportUrl = reportsApi.getSummaryExportUrl(queryParams);
        break;
      case 'trips':
        exportUrl = reportsApi.getTripsExportUrl(queryParams);
        break;
      case 'stops':
        exportUrl = reportsApi.getStopsExportUrl(queryParams);
        break;
      case 'events':
        exportUrl = reportsApi.getEventsExportUrl(queryParams);
        break;
    }

    try {
      // Remove /api prefix since axios baseURL already includes it
      const relativeUrl = exportUrl.replace(/^\/api/, '');
      const response = await api.get(relativeUrl, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Extract filename from Content-Disposition header or use default
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename=(.+)/);
      link.download = match ? match[1] : `rapport_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erreur lors de l\'export Excel');
    }
  };

  const handleExportPDF = () => {
    // Landscape for trips/stops (many columns), portrait for summary/events
    const useLandscape = reportType === 'trips' || reportType === 'stops';
    const doc = new jsPDF({ orientation: useLandscape ? 'landscape' : 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header — logo + title (no dark banner)
    const headerY = 14;
    const logoX = 14;
    let textX = 14;

    if (logoDataRef.current) {
      try {
        doc.addImage(logoDataRef.current, 'PNG', logoX, 8, 20, 20);
        textX = 38;
      } catch {
        // logo load failed, skip
      }
    }

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.text(`${t('reports.title')} — ${t(reportTypes.find((r) => r.id === reportType)?.label || '')}`, textX, headerY + 2);

    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(
      t('reports.pdf_period', { start: format(dateRange.start, 'dd/MM/yyyy'), end: format(dateRange.end, 'dd/MM/yyyy') }),
      textX,
      headerY + 10
    );
    doc.text(t('reports.pdf_generated_on', { date: format(new Date(), 'dd/MM/yyyy HH:mm') }), pageWidth - 55, headerY + 10);

    // Thin separator line
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(14, 32, pageWidth - 14, 32);

    // Table
    let tableData: (string | number)[][] = [];
    let headers: string[] = [];
    let footRow: (string | number)[] | null = null;

    switch (reportType) {
      case 'summary': {
        headers = [
          t('reports.th_vehicle'),
          t('reports.th_plate'),
          t('reports.th_distance'),
          t('reports.th_trips'),
          t('reports.th_stops'),
          t('reports.th_moving_time'),
          t('reports.th_stopped_time'),
          t('reports.th_max_speed'),
          t('reports.th_avg_speed'),
          t('reports.th_consumption'),
        ];
        const summaries = data as Summary[];
        tableData = summaries.map((item) => {
          const isEV = item.vehicleType === 'ELECTRIC';
          const consumption = isEV ? item.energyConsumed : item.fuelConsumed;
          return [
            item.deviceName,
            item.plate || '-',
            `${item.distance.toFixed(1)} km`,
            item.tripCount,
            item.stopCount,
            formatDuration(item.movingDuration),
            formatDuration(item.stoppedDuration),
            `${item.maxSpeed} km/h`,
            `${item.avgSpeed} km/h`,
            consumption != null ? `${consumption} ${isEV ? 'kWh' : 'L'}` : '-',
          ];
        });
        break;
      }
      case 'trips': {
        headers = [
          t('reports.th_vehicle'),
          t('reports.th_start_time'),
          t('reports.th_start_addr'),
          t('reports.th_end_time'),
          t('reports.th_end_addr'),
          t('reports.th_duration'),
          t('reports.th_distance'),
          t('reports.th_max_speed'),
          t('reports.th_avg_speed'),
        ];
        const trips = data as Trip[];
        tableData = trips.map((item) => [
          item.deviceName,
          formatDate(item.startTime),
          getAddress(item.startLatitude, item.startLongitude) || `${item.startLatitude.toFixed(5)}, ${item.startLongitude.toFixed(5)}`,
          formatDate(item.endTime),
          getAddress(item.endLatitude, item.endLongitude) || `${item.endLatitude.toFixed(5)}, ${item.endLongitude.toFixed(5)}`,
          formatDuration(item.duration),
          `${item.distance.toFixed(1)} km`,
          `${item.maxSpeed} km/h`,
          `${item.avgSpeed} km/h`,
        ]);
        const totalDuration = trips.reduce((acc, t) => acc + t.duration, 0);
        const totalDistance = trips.reduce((acc, t) => acc + t.distance, 0);
        footRow = ['TOTAL', '', '', '', '', formatDuration(totalDuration), `${totalDistance.toFixed(1)} km`, '', ''];
        break;
      }
      case 'stops': {
        headers = [
          t('reports.th_vehicle'),
          t('reports.th_start_time'),
          t('reports.th_end_time'),
          t('reports.th_duration'),
          t('reports.th_address'),
        ];
        const stops = data as Stop[];
        tableData = stops.map((item) => [
          item.deviceName,
          formatDate(item.startTime),
          formatDate(item.endTime),
          formatDuration(item.duration),
          getAddress(item.latitude, item.longitude) || `${item.latitude.toFixed(5)}, ${item.longitude.toFixed(5)}`,
        ]);
        const totalStopDuration = stops.reduce((acc, s) => acc + s.duration, 0);
        footRow = ['TOTAL', '', '', formatDuration(totalStopDuration), ''];
        break;
      }
      case 'events':
        headers = [
          t('reports.th_vehicle'),
          t('reports.th_type'),
          t('reports.th_date_time'),
          t('reports.th_position'),
        ];
        tableData = (data as Event[]).map((item) => [
          item.deviceName,
          item.type,
          formatDate(item.timestamp),
          item.latitude && item.longitude ? `${item.latitude.toFixed(5)}, ${item.longitude.toFixed(5)}` : '-',
        ]);
        break;
    }

    autoTable(doc, {
      head: [headers],
      body: tableData,
      foot: footRow ? [footRow] : undefined,
      startY: 36,
      theme: 'grid',
      headStyles: {
        fillColor: [6, 182, 212], // cyan-500
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      footStyles: {
        fillColor: [241, 245, 249],
        textColor: [15, 23, 42],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [241, 245, 249], // slate-100
      },
      styles: {
        fontSize: 7,
        cellPadding: 3,
      },
    });

    // Page footer
    const pageCount = (doc as unknown as { internal: { getNumberOfPages: () => number } }).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} / ${pageCount}`, pageWidth - 25, doc.internal.pageSize.getHeight() - 10);
    }

    doc.save(`rapport-${reportType}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  const toggleDevice = (deviceId: string) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  };

  const selectAllDevices = () => {
    setSelectedDevices(devices.map((d: { id: string }) => d.id));
  };

  const clearDevices = () => {
    setSelectedDevices([]);
  };

  // Calculate totals for summary
  const totals =
    reportType === 'summary'
      ? {
          distance: (data as Summary[]).reduce((acc, s) => acc + s.distance, 0),
          trips: (data as Summary[]).reduce((acc, s) => acc + s.tripCount, 0),
          stops: (data as Summary[]).reduce((acc, s) => acc + s.stopCount, 0),
          movingTime: (data as Summary[]).reduce((acc, s) => acc + s.movingDuration, 0),
          stoppedTime: (data as Summary[]).reduce((acc, s) => acc + s.stoppedDuration, 0),
        }
      : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
          <p className="text-gray-500 mt-1">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/reports/scheduled"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-xl hover:bg-purple-500/30 transition-colors"
          >
            <CalendarClock className="h-4 w-4" />
            {t('reports.scheduled_link')}
          </Link>
          <button
            onClick={handleExportPDF}
            disabled={isLoading || data.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-xl hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FilePlus className="h-4 w-4" />
            {t('reports.export_pdf')}
          </button>
          <button
            onClick={handleExportExcel}
            disabled={isLoading || data.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/30 rounded-xl hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {t('reports.export_excel')}
          </button>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {reportTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setReportType(type.id)}
            className={clsx(
              'glass-card p-4 text-left transition-all hover:scale-[1.02]',
              reportType === type.id
                ? 'border-cyan-500/50 bg-cyan-500/10'
                : 'border-gray-200/50 hover:border-gray-300'
            )}
          >
            <type.icon
              className={clsx(
                'h-6 w-6 mb-2',
                reportType === type.id ? 'text-cyan-400' : 'text-gray-400'
              )}
            />
            <div className={clsx('font-medium', reportType === type.id ? 'text-gray-900' : 'text-gray-600')}>
              {t(type.label)}
            </div>
            <div className="text-xs text-gray-400">{t(type.description)}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="glass-card p-4 relative z-40 overflow-visible">
        <div className="flex flex-wrap items-center gap-4">
          {/* Device Selector */}
          <div
            ref={deviceSelectorRef}
            className="relative"
          >
            <button
              onClick={() => setShowDeviceSelector(!showDeviceSelector)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <Truck className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {selectedDevices.length === 0
                  ? t('reports.all_vehicles')
                  : t('reports.n_vehicles', { count: selectedDevices.length })}
              </span>
              <ChevronDown className={clsx("h-4 w-4 text-gray-500 transition-transform", showDeviceSelector && "rotate-180")} />
            </button>

            {showDeviceSelector && (
              <div
                className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden"
              >
                <div className="p-3 border-b border-gray-200 flex gap-3 bg-gray-50">
                  <button
                    onClick={selectAllDevices}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    {t('reports.select_all')}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button onClick={clearDevices} className="text-xs text-gray-500 hover:text-gray-700">
                    {t('reports.clear')}
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {devices.map((device: { id: string; name: string; plate?: string }) => (
                    <label
                      key={device.id}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.id)}
                        onChange={() => toggleDevice(device.id)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">{device.name}</span>
                      {device.plate && <span className="text-xs text-gray-400">({device.plate})</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Date Presets */}
          <div className="flex flex-wrap gap-2">
            {datePresets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleDatePreset(preset)}
                className="px-3 py-1.5 text-sm bg-gray-50 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                {t(preset.label)}
              </button>
            ))}
          </div>

          {/* Custom Date Range */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={format(dateRange.start, 'yyyy-MM-dd')}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    start: startOfDay(new Date(e.target.value)),
                  }))
                }
                className="pl-10 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 text-gray-900 rounded-lg focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
            </div>
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={format(dateRange.end, 'yyyy-MM-dd')}
              onChange={(e) =>
                setDateRange((prev) => ({
                  ...prev,
                  end: endOfDay(new Date(e.target.value)),
                }))
              }
              className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 text-gray-900 rounded-lg focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
            />
          </div>

          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
          >
            {t('reports.generate')}
          </button>

          <button
            onClick={() => setShowCharts(!showCharts)}
            className={clsx(
              'ml-auto px-3 py-1.5 text-sm rounded-lg transition-colors',
              showCharts
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-gray-50 text-gray-500 border border-gray-200'
            )}
          >
            <TrendingUp className="h-4 w-4 inline mr-1" />
            {t('reports.charts')}
          </button>
        </div>
      </div>

      {/* Summary Stats Cards */}
      {reportType === 'summary' && totals && !isLoading && data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 relative z-10">
          <div className="glass-card p-4">
            <div className="text-gray-500 text-sm">{t('reports.stat_total_distance')}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{totals.distance.toFixed(1)} km</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-gray-500 text-sm">{t('reports.stat_trips')}</div>
            <div className="text-2xl font-bold text-cyan-400 mt-1">{totals.trips}</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-gray-500 text-sm">{t('reports.stat_stops')}</div>
            <div className="text-2xl font-bold text-amber-400 mt-1">{totals.stops}</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-gray-500 text-sm">{t('reports.stat_moving_time')}</div>
            <div className="text-2xl font-bold text-green-400 mt-1">{formatDuration(totals.movingTime)}</div>
          </div>
          <div className="glass-card p-4">
            <div className="text-gray-500 text-sm">{t('reports.stat_stopped_time')}</div>
            <div className="text-2xl font-bold text-purple-400 mt-1">{formatDuration(totals.stoppedTime)}</div>
          </div>
        </div>
      )}

      {/* Charts */}
      {showCharts && !isLoading && data.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {reportType === 'summary' && (
            <>
              {/* Distance by vehicle */}
              <div className="glass-card p-4">
                <h3 className="text-gray-900 font-medium mb-4">{t('reports.chart_distance_per_vehicle')}</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <BarChart data={distanceChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="distance" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Distance (km)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Time distribution */}
              <div className="glass-card p-4">
                <h3 className="text-gray-900 font-medium mb-4">{t('reports.chart_time_split')}</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <PieChart>
                      <Pie
                        data={timeDistributionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                        labelLine={{ stroke: '#64748b' }}
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatDuration(Number(value))}
                        contentStyle={{
                          backgroundColor: '#1e293b',
                          border: '1px solid #334155',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {reportType === 'trips' && dailyDistanceData.length > 0 && (
            <>
              {/* Daily distance */}
              <div className="glass-card p-4 lg:col-span-2">
                <h3 className="text-gray-900 font-medium mb-4">{t('reports.chart_daily_distance')}</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                    <AreaChart data={dailyDistanceData}>
                      <defs>
                        <linearGradient id="colorDistance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="distance"
                        stroke="#06b6d4"
                        fill="url(#colorDistance)"
                        name="Distance (km)"
                      />
                      <Line type="monotone" dataKey="trajets" stroke="#8b5cf6" name="Nb trajets" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Results Table */}
      <div ref={reportRef} className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('reports.no_data_title')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('reports.no_data_subtitle')}</p>
          </div>
        ) : (
          <>
            {/* Summary Report */}
            {reportType === 'summary' && (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_vehicle')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_plate')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_distance')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_trips')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_stops')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_moving_time')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_stopped_time')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_max_speed')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_avg_speed')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_consumption')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(data as Summary[]).map((item) => {
                      const isEV = item.vehicleType === 'ELECTRIC';
                      const consumption = isEV ? item.energyConsumed : item.fuelConsumed;
                      return (
                        <tr key={item.deviceId} className="hover:bg-gray-100/30 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.deviceName}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{item.plate || '-'}</td>
                          <td className="px-4 py-3 text-sm text-cyan-400 text-right font-medium">
                            {item.distance.toFixed(1)} km
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.tripCount}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.stopCount}</td>
                          <td className="px-4 py-3 text-sm text-green-400 text-right">
                            {formatDuration(item.movingDuration)}
                          </td>
                          <td className="px-4 py-3 text-sm text-amber-400 text-right">
                            {formatDuration(item.stoppedDuration)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.maxSpeed} km/h</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.avgSpeed} km/h</td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {consumption != null ? (
                              <span className={isEV ? 'text-green-500' : 'text-amber-500'}>
                                {consumption} {isEV ? 'kWh' : 'L'}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50/50">
                    {(() => {
                      const summaries = data as Summary[];
                      const totalFuel = summaries.reduce((acc, s) => acc + (s.fuelConsumed || 0), 0);
                      const totalEnergy = summaries.reduce((acc, s) => acc + (s.energyConsumed || 0), 0);
                      const parts: string[] = [];
                      if (totalFuel > 0) parts.push(`${(Math.round(totalFuel * 10) / 10)} L`);
                      if (totalEnergy > 0) parts.push(`${(Math.round(totalEnergy * 10) / 10)} kWh`);
                      return (
                        <tr>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-sm font-bold text-cyan-400 text-right">
                            {summaries.reduce((acc, s) => acc + s.distance, 0).toFixed(1)} km
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                            {summaries.reduce((acc, s) => acc + s.tripCount, 0)}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                            {summaries.reduce((acc, s) => acc + s.stopCount, 0)}
                          </td>
                          <td className="px-4 py-3" colSpan={4}></td>
                          <td className="px-4 py-3 text-sm font-bold text-right">
                            {parts.length > 0 ? parts.join(' + ') : '-'}
                          </td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            )}

            {/* Trips Report */}
            {reportType === 'trips' && (
              <div className="overflow-x-auto">
                {addressesLoading && (
                  <div className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 bg-gray-50 border-b border-gray-200">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('reports.resolving_addresses')}
                  </div>
                )}
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_vehicle')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_start_time')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_start_address')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_end_time')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_end_address')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_duration')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_distance')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_max_speed')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_avg_speed')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(data as Trip[]).map((trip, idx) => (
                      <tr key={idx} className="hover:bg-gray-100/30 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{trip.deviceName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            {formatDate(trip.startTime)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px]">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-green-500 flex-shrink-0" />
                            <span className="truncate" title={getAddress(trip.startLatitude, trip.startLongitude) || ''}>
                              {getAddress(trip.startLatitude, trip.startLongitude) || (
                                <span className="text-gray-300 italic">{trip.startLatitude.toFixed(5)}, {trip.startLongitude.toFixed(5)}</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            {formatDate(trip.endTime)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px]">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-red-500 flex-shrink-0" />
                            <span className="truncate" title={getAddress(trip.endLatitude, trip.endLongitude) || ''}>
                              {getAddress(trip.endLatitude, trip.endLongitude) || (
                                <span className="text-gray-300 italic">{trip.endLatitude.toFixed(5)}, {trip.endLongitude.toFixed(5)}</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatDuration(trip.duration)}</td>
                        <td className="px-4 py-3 text-sm text-cyan-400 text-right font-medium">
                          {trip.distance.toFixed(1)} km
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Gauge className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-900">{trip.maxSpeed} km/h</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{trip.avgSpeed} km/h</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50/50">
                    <tr>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                      <td className="px-4 py-3" colSpan={4}></td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                        {formatDuration((data as Trip[]).reduce((acc, t) => acc + t.duration, 0))}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-cyan-400 text-right">
                        {(data as Trip[]).reduce((acc, t) => acc + t.distance, 0).toFixed(1)} km
                      </td>
                      <td className="px-4 py-3" colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Stops Report */}
            {reportType === 'stops' && (
              <div className="overflow-x-auto">
                {addressesLoading && (
                  <div className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 bg-gray-50 border-b border-gray-200">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('reports.resolving_addresses')}
                  </div>
                )}
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_vehicle')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_start_time')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_end_time')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_duration')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_address')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(data as Stop[]).map((stop, idx) => (
                      <tr key={idx} className="hover:bg-gray-100/30 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{stop.deviceName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(stop.startTime)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(stop.endTime)}</td>
                        <td className="px-4 py-3 text-sm text-amber-400 text-right font-medium">
                          {formatDuration(stop.duration)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-[250px]">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-red-400 flex-shrink-0" />
                            <span className="truncate" title={getAddress(stop.latitude, stop.longitude) || `${stop.latitude.toFixed(5)}, ${stop.longitude.toFixed(5)}`}>
                              {getAddress(stop.latitude, stop.longitude) || (
                                <span className="text-gray-300 italic">{stop.latitude.toFixed(5)}, {stop.longitude.toFixed(5)}</span>
                              )}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50/50">
                    <tr>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">TOTAL</td>
                      <td className="px-4 py-3" colSpan={2}></td>
                      <td className="px-4 py-3 text-sm font-bold text-amber-400 text-right">
                        {formatDuration((data as Stop[]).reduce((acc, s) => acc + s.duration, 0))}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Events Report */}
            {reportType === 'events' && (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_vehicle')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_type')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_date_time')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('reports.th_position')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(data as Event[]).map((event) => (
                      <tr key={event.id} className="hover:bg-gray-100/30 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{event.deviceName}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                            <AlertTriangle className="h-3 w-3" />
                            {event.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(event.timestamp)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {event.latitude && event.longitude ? (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              {event.latitude.toFixed(5)}, {event.longitude.toFixed(5)}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats bar */}
      {!isLoading && data.length > 0 && (
        <div className="text-sm text-gray-400 text-center">
          {t('reports.results_count', {
            count: data.length,
            start: format(dateRange.start, 'dd/MM/yyyy', { locale: dateLocale }),
            end: format(dateRange.end, 'dd/MM/yyyy', { locale: dateLocale }),
          })}
        </div>
      )}
    </div>
  );
}
